import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { TRIAL_EXTENSION_DAYS, TRIAL_DAYS } from "../../config/subscriptionPlans";
import {
    approveTrialExtension,
    approveSchoolVerification,
    fetchSystemSchool,
    formatDate,
    rejectTrialExtension,
    rejectSchoolVerification,
    subscriptionStatusColor,
    updateSchoolSubscription,
    updateSystemSchool,
} from "../../utils/systemApi";
import { formatPriceMwk, getPlanById, SUBSCRIPTION_PLANS } from "../../config/subscriptionPlans";
import Popup from "../../components/Popup";

const MALAWI_REGIONS = ["Northern", "Central", "Southern"];
const OWNERSHIP_TYPES = ["Government", "Private", "Mission/Church", "Community"];

export default function SchoolDetail() {
    const { schoolId } = useParams();
    const [school, setSchool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const [form, setForm] = useState({});
    const [planAction, setPlanAction] = useState("activate");
    const [selectedPlan, setSelectedPlan] = useState("starter");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchSystemSchool(schoolId);
            if (data?.message && !data?.schoolName) {
                setError(data.message);
                setSchool(null);
                return;
            }
            setSchool(data);
            setSelectedPlan(data.subscriptionPlan || "starter");
            setForm({
                directorName: data.name || "",
                directorEmail: data.email || "",
                schoolName: data.schoolName || "",
                schoolDistrict: data.schoolDistrict || "",
                schoolRegion: data.schoolRegion || "",
                schoolOwnership: data.schoolOwnership || "",
                schoolCode: data.schoolCode || "",
                contactPhone: data.contactPhone || "",
                contactEmail: data.contactEmail || "",
                contactAddress: data.contactAddress || "",
                whatsappNumber: data.whatsappNumber || "",
            });
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not load school");
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleField = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const saveContact = async () => {
        setSaving(true);
        try {
            const updated = await updateSystemSchool(schoolId, form);
            setSchool(updated);
            setMessage(updated.message || "School updated");
            setShowPopup(true);
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Update failed");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const reviewTrialExtension = async (action) => {
        setSaving(true);
        try {
            const updated =
                action === "approve"
                    ? await approveTrialExtension(schoolId)
                    : await rejectTrialExtension(schoolId);
            setSchool(updated);
            setMessage(updated.message || (action === "approve" ? "Extension approved" : "Request rejected"));
            setShowPopup(true);
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not update request");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const reviewSchoolVerification = async (action) => {
        setSaving(true);
        try {
            const updated =
                action === "approve"
                    ? await approveSchoolVerification(schoolId)
                    : await rejectSchoolVerification(schoolId);
            setSchool(updated);
            setMessage(updated.message || (action === "approve" ? "School approved — trial started" : "Registration rejected"));
            setShowPopup(true);
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Could not update verification");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const runSubscriptionAction = async () => {
        setSaving(true);
        try {
            const payload = {
                action: planAction,
                subscriptionPlan: selectedPlan,
            };
            if (planAction === "extend_trial") payload.days = TRIAL_EXTENSION_DAYS;
            const updated = await updateSchoolSubscription(schoolId, payload);
            setSchool(updated);
            setMessage(updated.message || "Subscription updated");
            setShowPopup(true);
        } catch (e) {
            setMessage(e.response?.data?.message || e.message || "Subscription update failed");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !school) {
        return (
            <Stack spacing={2}>
                <Button component={RouterLink} to="/System/schools" startIcon={<ArrowBackIcon />}>
                    Back to schools
                </Button>
                <Alert severity="error">{error || "School not found"}</Alert>
            </Stack>
        );
    }

    const plan = getPlanById(school.subscriptionPlan);

    return (
        <Stack spacing={3}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Button component={RouterLink} to="/System/schools" startIcon={<ArrowBackIcon />}>
                    Schools
                </Button>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{school.schoolName}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip size="small" label={plan.name} />
                        <Chip size="small" color={subscriptionStatusColor(school.subscriptionStatus)} label={school.subscriptionStatus} />
                        {String(school.verificationStatus || "").toLowerCase() === "pending" && (
                            <Chip size="small" color="warning" label="Awaiting verification" />
                        )}
                        {school.paymentPendingAt && <Chip size="small" color="warning" label="Payment pending" />}
                    </Stack>
                </Box>
            </Stack>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary">Enrolment</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{school.studentCount ?? 0}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {school.teacherCount ?? 0} teachers · {school.classCount ?? 0} classes
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2"><strong>Plan price:</strong> {formatPriceMwk(plan.priceMwk)}</Typography>
                            <Typography variant="body2"><strong>Trial ends:</strong> {formatDate(school.trialEndsAt)}</Typography>
                            <Typography variant="body2"><strong>Subscription ends:</strong> {formatDate(school.subscriptionEndsAt)}</Typography>
                            <Typography variant="body2"><strong>Registered:</strong> {school.schoolType} · {school.curriculumType}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Contact & registration</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Director name" value={form.directorName} onChange={handleField("directorName")} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Director email" value={form.directorEmail} onChange={handleField("directorEmail")} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth size="small" label="School name" value={form.schoolName} onChange={handleField("schoolName")} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="District" value={form.schoolDistrict} onChange={handleField("schoolDistrict")} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Region</InputLabel>
                                        <Select label="Region" value={form.schoolRegion} onChange={handleField("schoolRegion")}>
                                            <MenuItem value="">—</MenuItem>
                                            {MALAWI_REGIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Ownership</InputLabel>
                                        <Select label="Ownership" value={form.schoolOwnership} onChange={handleField("schoolOwnership")}>
                                            {OWNERSHIP_TYPES.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="MoE school code" value={form.schoolCode} onChange={handleField("schoolCode")} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Phone" value={form.contactPhone} onChange={handleField("contactPhone")} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="Public contact email" value={form.contactEmail} onChange={handleField("contactEmail")} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth size="small" label="Physical address" value={form.contactAddress} onChange={handleField("contactAddress")} multiline minRows={2} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth size="small" label="WhatsApp" value={form.whatsappNumber} onChange={handleField("whatsappNumber")} />
                                </Grid>
                            </Grid>
                            <Button variant="contained" sx={{ mt: 2 }} disabled={saving} onClick={saveContact}>
                                Save contact details
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {String(school.verificationStatus || "").toLowerCase() === "pending" ? (
                <Alert severity="warning">
                    <Typography variant="subtitle2" fontWeight={700}>
                        Registration awaiting verification
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Requested {formatDate(school.verificationRequestedAt)}. Approve to start the {TRIAL_DAYS}-day free trial automatically.
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={saving}
                            onClick={() => reviewSchoolVerification("approve")}
                        >
                            Approve & start trial
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={saving}
                            onClick={() => reviewSchoolVerification("reject")}
                        >
                            Reject registration
                        </Button>
                    </Stack>
                </Alert>
            ) : null}

            {school.trialExtensionRequestStatus === "pending" ? (
                <Alert severity="warning" sx={{ mb: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                        Pending trial extension request
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Requested {formatDate(school.trialExtensionRequestedAt)}
                        {school.trialExtensionMessage ? ` — “${school.trialExtensionMessage}”` : ""}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            disabled={saving}
                            onClick={() => reviewTrialExtension("approve")}
                        >
                            Approve (+1 month)
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={saving}
                            onClick={() => reviewTrialExtension("reject")}
                        >
                            Reject
                        </Button>
                    </Stack>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Each school may only receive one trial extension.
                    </Typography>
                </Alert>
            ) : null}

            {school.trialExtensionUsed ? (
                <Alert severity="info">This school has already used its one trial extension.</Alert>
            ) : null}

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Subscription management</Typography>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-end" }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Plan</InputLabel>
                            <Select label="Plan" value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
                                {SUBSCRIPTION_PLANS.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>{p.name} — {formatPriceMwk(p.priceMwk)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Action</InputLabel>
                            <Select label="Action" value={planAction} onChange={(e) => setPlanAction(e.target.value)}>
                                <MenuItem value="activate">Activate after payment</MenuItem>
                                <MenuItem value="upgrade">Upgrade & activate</MenuItem>
                                <MenuItem value="change_plan">Change plan only</MenuItem>
                                <MenuItem value="extend_trial" disabled={school.trialExtensionUsed}>
                                    Extend trial (+1 month, uses one-time extension)
                                </MenuItem>
                                <MenuItem value="approve_trial_extension" disabled={school.trialExtensionRequestStatus !== "pending"}>
                                    Approve pending extension request
                                </MenuItem>
                                <MenuItem value="reject_trial_extension" disabled={school.trialExtensionRequestStatus !== "pending"}>
                                    Reject pending extension request
                                </MenuItem>
                                <MenuItem value="approve_verification" disabled={String(school.verificationStatus || "").toLowerCase() !== "pending"}>
                                    Approve school registration
                                </MenuItem>
                                <MenuItem value="reject_verification" disabled={String(school.verificationStatus || "").toLowerCase() !== "pending"}>
                                    Reject school registration
                                </MenuItem>
                                <MenuItem value="mark_pending">Mark payment pending</MenuItem>
                                <MenuItem value="clear_pending">Clear payment pending</MenuItem>
                                <MenuItem value="expire">Expire subscription</MenuItem>
                            </Select>
                        </FormControl>
                        <Button variant="contained" color="secondary" disabled={saving} onClick={runSubscriptionAction}>
                            Apply
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {(school.subscriptionPayments || []).length > 0 && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Payment history</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Plan</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Date</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {school.subscriptionPayments.map((p) => (
                                    <TableRow key={p.id || p._id}>
                                        <TableCell>{p.referenceNumber}</TableCell>
                                        <TableCell>{getPlanById(p.planId).name}</TableCell>
                                        <TableCell>{formatPriceMwk(p.amount)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" color={subscriptionStatusColor(p.status)} label={p.status} />
                                        </TableCell>
                                        <TableCell>{formatDate(p.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Stack>
    );
}
